/**
 * Maps the CRM `CreateCaseWebsite` response into a user-facing outcome.
 *
 * The CRM signals a duplicate/open case with `Success: true` but a message like
 * "Association Request already registered" — which shouldn't read as a fresh
 * "Thank you". This normalizes the three outcomes both lead forms care about.
 */
interface CrmEnquiryResponse {
  Success?: boolean;
  Message?: string | null;
  ErrorMessage?: string | null;
  ReturnValue?: { ticketnumber?: string | null } | null;
}

export type EnquiryOutcome = 'success' | 'duplicate' | 'error';

export interface EnquiryResult {
  outcome: EnquiryOutcome;
  title: string;
  message: string;
  ticket?: string;
}

export function mapEnquiryResponse(data: CrmEnquiryResponse | null | undefined): EnquiryResult {
  const raw = (data?.Message || data?.ErrorMessage || '').trim();
  const ticket = data?.ReturnValue?.ticketnumber || undefined;

  // Duplicate / already-open case (CRM returns Success:true + "already registered").
  if (/already\s+(registered|exists?|open)/i.test(raw)) {
    return {
      outcome: 'duplicate',
      title: 'Request already submitted',
      message:
        'You already have an open request with us. Our team will resolve it before a new one can be submitted.',
      ticket,
    };
  }

  if (data?.Success === false) {
    return {
      outcome: 'error',
      title: 'Something went wrong',
      message: raw || 'We couldn’t submit your enquiry. Please try again.',
    };
  }

  return {
    outcome: 'success',
    title: 'Enquiry submitted',
    message: 'Thank you - our expert team will get back to you shortly.',
    ticket,
  };
}
